/**
 * BlockNode - Unified Rich Text Content Node
 *
 * A collaborative document node that combines:
 * - Rich text editing (Tiptap/ProseMirror)
 * - Flow connectivity (receives data from upstream nodes)
 * - Real-time collaboration (via Yjs)
 * - AI agent integration (agents can stream content)
 *
 * Design principles:
 * - Beautiful multi-layered shadows for depth
 * - Clean hover-based controls
 * - Data flow visualization via connection chips
 * - Seamless integration with Deep Agents workflow
 */

import { memo, useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { Handle, Position, NodeResizer, useStore, type NodeProps, type Node } from '@xyflow/react';
import {
  ASPECT_RATIO_META,
  BLOCK_FORMAT_PRESETS,
  DEFAULT_BLOCK_FORMAT,
  calculateBlockDimensions,
  getEffectiveRatio,
  type AspectRatioPreset,
  type BlockConnection,
  type BlockFormatConfig,
  type BlockNodeData,
  type KnowledgeCitation,
  type Orientation,
} from '@deep-agents/shared-types';
import { RichTextEditor, type RichTextEditorHandle } from '../editor/RichTextEditor';
import { useCurrentUser } from '../../hooks/useCurrentUser';
import type * as Y from 'yjs';
import { getNodeContent, getNodeLabel } from '../../utils/nodeContent';

// ============================================================================
// MAIN COMPONENT
// ============================================================================

/**
 * BlockNode Component
 *
 * Props come from React Flow's node data structure.
 * Content updates are synced via parent callbacks (which connect to Yjs).
 */
type BlockNodeType = Node<BlockNodeData, 'block'>;

type BlockNodeRuntimeData = BlockNodeData & {
  nodesMap?: Y.Map<Node>;
  makeChange?: (fn: () => void) => void;
};

function FormatIcon({
  aspectRatio,
  orientation,
  isSelected,
}: {
  aspectRatio: AspectRatioPreset;
  orientation: Orientation;
  isSelected?: boolean;
}) {
  const ratio = getEffectiveRatio({ aspectRatio, orientation });
  const isFluid = aspectRatio === 'fluid';
  const isDocument = aspectRatio === 'a4' || aspectRatio === 'a5' || aspectRatio === 'letter';

  const boxSize = 18;
  const minSide = 6;
  let width = boxSize;
  let height = boxSize;

  if (ratio) {
    if (ratio >= 1) {
      width = boxSize;
      height = Math.max(minSide, Math.round(boxSize / ratio));
    } else {
      height = boxSize;
      width = Math.max(minSide, Math.round(boxSize * ratio));
    }
  }

  const x = (24 - width) / 2;
  const y = (24 - height) / 2;
  const stroke = isSelected ? '#4f46e5' : '#64748b';

  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <rect
        x={x}
        y={y}
        width={width}
        height={height}
        rx="2"
        stroke={stroke}
        strokeWidth="1.6"
        strokeDasharray={isFluid ? '3 2' : undefined}
      />
      {isDocument && (
        <path
          d={`M${x + width - 4} ${y} L${x + width} ${y + 4}`}
          stroke={stroke}
          strokeWidth="1.6"
          strokeLinecap="round"
        />
      )}
    </svg>
  );
}

export const BlockNode = memo(({ id, data, selected, dragging }: NodeProps<BlockNodeType>) => {
  // Destructure data with defaults
  const {
    title = '',
    ydoc,
    awareness,
    boardId,
    onTitleChange,
    format,
    nodesMap,
    makeChange,
    citations,
  } = data as BlockNodeRuntimeData;

  // Get current user for collaboration cursors
  const { user } = useCurrentUser();
  const edges = useStore((state) => state.edges);

  // ==================== LOCAL STATE ====================

  // Local title state (synced to parent on change)
  const [localTitle, setLocalTitle] = useState(title);

  const [localFormat, setLocalFormat] = useState<BlockFormatConfig>(format ?? DEFAULT_BLOCK_FORMAT);
  const [isFormatMenuOpen, setIsFormatMenuOpen] = useState(false);
  const [nodesVersion, setNodesVersion] = useState(0);
  const [contentVersion, setContentVersion] = useState(0);

  // Hover state - show/hide controls and handles
  const [isHovered, setIsHovered] = useState(false);

  // Drag-to-reorder state for incoming connection chips
  const [draggedChipIndex, setDraggedChipIndex] = useState<number | null>(null);

  const formatMenuRef = useRef<HTMLDivElement>(null);
  const editorRef = useRef<RichTextEditorHandle | null>(null);

  // Container ref
  const containerRef = useRef<HTMLDivElement>(null);

  // ==================== DYNAMIC SHADOW ====================

  /**
   * Get shadow style based on node state
   * Matches StickyNoteNode and ShapeNode behavior for consistency
   */
  const getShadowStyle = () => {
    if (dragging) {
      return 'var(--shadow-elevation-high)';
    }
    if (selected) {
      return 'var(--shadow-elevation-medium)';
    }
    return 'var(--shadow-elevation-low)';
  };

  // ==================== HANDLERS ====================

  /**
   * Handle title changes
   */
  const handleTitleChange = useCallback((newTitle: string) => {
    setLocalTitle(newTitle);
    onTitleChange?.(newTitle);
  }, [onTitleChange]);

  useEffect(() => {
    const next = format ?? DEFAULT_BLOCK_FORMAT;
    setLocalFormat((prev) => {
      if (prev.aspectRatio === next.aspectRatio && prev.orientation === next.orientation) {
        return prev;
      }
      return next;
    });
  }, [format?.aspectRatio, format?.orientation]);

  useEffect(() => {
    if (!isFormatMenuOpen) return;

    const handleClick = (event: MouseEvent) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      if (formatMenuRef.current?.contains(target)) return;
      setIsFormatMenuOpen(false);
    };

    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [isFormatMenuOpen]);

  useEffect(() => {
    if (!nodesMap) return;
    let rafId = 0;
    const handleUpdate = () => {
      if (rafId) return;
      rafId = window.requestAnimationFrame(() => {
        rafId = 0;
        setNodesVersion((prev) => prev + 1);
      });
    };
    nodesMap.observe(handleUpdate);
    return () => {
      nodesMap.unobserve(handleUpdate);
      if (rafId) {
        window.cancelAnimationFrame(rafId);
      }
    };
  }, [nodesMap]);

  const incomingEdges = useMemo(
    () => edges.filter((edge) => edge.target === id),
    [edges, id]
  );

  const connectedNodes = useMemo(() => {
    if (!nodesMap) return [];
    const result: Node[] = [];
    const seen = new Set<string>();
    for (const edge of incomingEdges) {
      const sourceNode = nodesMap.get(edge.source);
      if (!sourceNode || seen.has(sourceNode.id)) {
        continue;
      }
      seen.add(sourceNode.id);
      result.push(sourceNode);
    }
    return result;
  }, [incomingEdges, nodesMap, nodesVersion]);

  const indirectNodes = useMemo(() => {
    if (!nodesMap) return [];
    const result: Node[] = [];
    const seen = new Set<string>(connectedNodes.map((node) => node.id));

    connectedNodes
      .filter((node) => node.type === 'generator')
      .forEach((generatorNode) => {
        edges.forEach((edge) => {
          if (edge.target !== generatorNode.id) return;
          const sourceNode = nodesMap.get(edge.source);
          if (!sourceNode || seen.has(sourceNode.id)) return;
          seen.add(sourceNode.id);
          result.push(sourceNode);
        });
      });

    return result;
  }, [connectedNodes, edges, nodesMap, nodesVersion]);

  const mentionSourceNodes = useMemo(() => {
    const map = new Map<string, Node>();
    connectedNodes.forEach((node) => map.set(node.id, node));
    indirectNodes.forEach((node) => map.set(node.id, node));
    return Array.from(map.values());
  }, [connectedNodes, indirectNodes]);

  const observedBlockIds = useMemo(
    () => mentionSourceNodes.filter((node) => node.type === 'block').map((node) => node.id),
    [mentionSourceNodes]
  );

  useEffect(() => {
    if (!ydoc || observedBlockIds.length === 0) return;
    let rafId = 0;
    const schedule = () => {
      if (rafId) return;
      rafId = window.requestAnimationFrame(() => {
        rafId = 0;
        setContentVersion((prev) => prev + 1);
      });
    };
    const observers: Array<{ fragment: Y.XmlFragment; handler: () => void }> = [];
    observedBlockIds.forEach((blockId) => {
      const fragment = ydoc.getXmlFragment(`richtext-${blockId}`);
      const handler = () => schedule();
      fragment.observe(handler);
      observers.push({ fragment, handler });
    });
    return () => {
      observers.forEach(({ fragment, handler }) => fragment.unobserve(handler));
      if (rafId) {
        window.cancelAnimationFrame(rafId);
      }
    };
  }, [ydoc, observedBlockIds]);

  const connections = useMemo<BlockConnection[]>(() => {
    if (!nodesMap) return [];
    return connectedNodes.map((node) => {
      const label = getNodeLabel(node);
      const { textContent, imageUrl } = getNodeContent(node, ydoc);
      return {
        nodeId: node.id,
        nodeLabel: label,
        nodeType: node.type ?? 'unknown',
        textContent,
        imageUrl,
      };
    });
  }, [connectedNodes, nodesMap, ydoc, contentVersion]);

  const mentionConnections = useMemo(() => {
    if (!nodesMap) return [];
    return mentionSourceNodes.map((node) => {
      const label = getNodeLabel(node);
      const { textContent, imageUrl } = getNodeContent(node, ydoc);
      return {
        nodeId: node.id,
        nodeLabel: label,
        nodeType: node.type,
        textContent,
        imageUrl,
      } as BlockConnection;
    });
  }, [mentionSourceNodes, nodesMap, ydoc, contentVersion]);

  const mentionSuggestions = useMemo(
    () => mentionConnections.map((conn) => ({
      id: conn.nodeId,
      label: conn.nodeLabel,
      type: conn.nodeType,
      content: conn.textContent,
      imageUrl: conn.imageUrl,
    })),
    [mentionConnections]
  );

  /**
   * Handle click on incoming connection chip to insert data into document.
   *
   * Note: This will be handled by the RichTextEditor's mention system
   * in a future update. For now, chips show available data sources.
   */
  const handleInsertData = useCallback((conn: BlockConnection) => {
    const label = conn.nodeLabel || conn.nodeId;
    editorRef.current?.insertMention(conn.nodeId, label);
  }, []);

  const handleFormatChange = useCallback((next: BlockFormatConfig) => {
    setLocalFormat(next);
    setIsFormatMenuOpen(false);

    if (!nodesMap || !makeChange) return;

    makeChange(() => {
      const existing = nodesMap.get(id);
      if (!existing) return;
      nodesMap.set(id, {
        ...existing,
        data: {
          ...existing.data,
          format: next,
        },
      });
    });
  }, [id, nodesMap, makeChange]);

  const handleOrientationChange = useCallback((orientation: Orientation) => {
    handleFormatChange({
      ...localFormat,
      orientation,
    });
  }, [handleFormatChange, localFormat]);

  /**
   * Handle drag start for chip reordering
   */
  const handleDragStart = useCallback((e: React.DragEvent, index: number) => {
    setDraggedChipIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  }, []);

  /**
   * Handle drop for chip reordering
   */
  const handleDrop = useCallback((e: React.DragEvent, targetIndex: number) => {
    e.preventDefault();
    if (draggedChipIndex === null || draggedChipIndex === targetIndex) return;

    // Reorder connections array
    const newConnections = [...connections];
    const [removed] = newConnections.splice(draggedChipIndex, 1);
    newConnections.splice(targetIndex, 0, removed);

    // Persist via callback
    data.onIncomingConnectionsChange?.(newConnections);
    setDraggedChipIndex(null);
  }, [draggedChipIndex, connections, data]);

  // ==================== COMPUTED VALUES ====================

  // Show controls when hovered OR selected
  const showControls = isHovered || selected;

  const formatMeta = ASPECT_RATIO_META[localFormat.aspectRatio];
  const dimensions = calculateBlockDimensions(localFormat);
  const minHeight = typeof dimensions.height === 'number' ? dimensions.height : 200;
  const isFixedSize = dimensions.height !== 'auto';
  const showOrientationToggle =
    formatMeta.canFlip &&
    localFormat.aspectRatio !== 'fluid' &&
    formatMeta.ratio !== 1;
  const orientationLabel = showOrientationToggle
    ? localFormat.orientation === 'portrait'
      ? 'P'
      : 'L'
    : '';

  // ==================== RENDER ====================

  return (
    <div
      className="relative transition-all duration-300"
      style={{ padding: '0 8px' }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* ==================== CONNECTION HANDLES ==================== */}
      {/* DEBUG: Always fully visible handles */}
      <Handle
        type="target"
        position={Position.Left}
        id="input"
        className="!w-4 !h-4 !bg-slate-400 !border-2 !border-white !opacity-100"
        style={{ top: '50%', pointerEvents: 'all', zIndex: 100 }}
        title="Receive content from upstream nodes"
      />
      <Handle
        type="source"
        position={Position.Right}
        id="output"
        className="!w-4 !h-4 !bg-indigo-500 !border-2 !border-white !opacity-100"
        style={{ top: '50%', pointerEvents: 'all', zIndex: 100 }}
        title="Send content to downstream nodes"
      />

      {/* ==================== FLOATING HEADER (Above Card) ==================== */}
      {/* Positioned absolutely so it doesn't affect node bounding box */}
      {/* Only visible on hover or when selected */}
      <div
        className={`
          absolute bottom-full left-0 right-0 pb-2
          transition-opacity duration-200
          z-30
          ${showControls ? 'opacity-100' : 'opacity-0 pointer-events-none'}
        `}
      >
        {/* Title Input as Node Name Chip */}
        <div className="flex items-center gap-2 px-1">
          <div
            className="nodrag nopan"
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
          >
            <input
              type="text"
              value={localTitle}
              onChange={(e) => handleTitleChange(e.target.value)}
              placeholder="Untitled Block"
              className="
                px-2.5 py-1 text-[12px] font-semibold text-indigo-700
                bg-gradient-to-r from-indigo-50 to-violet-50
                rounded-lg border border-indigo-100/50
                outline-none focus:border-indigo-300 focus:ring-1 focus:ring-indigo-200
                placeholder:text-indigo-300 placeholder:font-medium
                min-w-[80px] max-w-[200px] truncate
                transition-all
              "
              style={{ width: `${Math.max(80, Math.min(200, (localTitle.length || 12) * 8))}px` }}
            />
          </div>

          <div
            ref={formatMenuRef}
            className="relative nodrag nopan"
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => setIsFormatMenuOpen((prev) => !prev)}
              className="
                flex items-center gap-1.5 px-2 py-1
                rounded-lg border border-slate-200
                bg-white text-slate-700 text-[11px] font-medium
                shadow-sm hover:border-indigo-300 hover:text-indigo-700 transition-colors
              "
              title={formatMeta.description}
            >
              <FormatIcon
                aspectRatio={localFormat.aspectRatio}
                orientation={localFormat.orientation}
                isSelected={false}
              />
              <span>{formatMeta.label}</span>
              {orientationLabel && (
                <span className="text-[10px] font-semibold text-slate-500">{orientationLabel}</span>
              )}
            </button>

            {isFormatMenuOpen && (
              <div className="absolute left-0 z-50 mt-2 w-64 rounded-xl border border-slate-200 bg-white p-2 shadow-lg">
                <div className="grid grid-cols-3 gap-2">
                  {BLOCK_FORMAT_PRESETS.map((preset) => {
                    const isActive = preset.aspectRatio === localFormat.aspectRatio;
                    return (
                      <button
                        key={preset.id}
                        type="button"
                        onClick={() => handleFormatChange({
                          aspectRatio: preset.aspectRatio,
                          orientation: preset.orientation,
                        })}
                        className={`
                          flex flex-col items-center gap-1 rounded-lg border px-2 py-2 text-[10px] font-semibold
                          ${isActive ? 'border-indigo-400 bg-indigo-50 text-indigo-700' : 'border-transparent text-slate-600 hover:border-slate-200 hover:bg-slate-50'}
                        `}
                        title={ASPECT_RATIO_META[preset.aspectRatio].description}
                      >
                        <FormatIcon
                          aspectRatio={preset.aspectRatio}
                          orientation={preset.orientation}
                          isSelected={isActive}
                        />
                        <span>{preset.label}</span>
                      </button>
                    );
                  })}
                </div>

                {showOrientationToggle && (
                  <div className="mt-2 flex items-center justify-between gap-2 rounded-lg border border-slate-200 bg-slate-50 p-1">
                    <button
                      type="button"
                      onClick={() => handleOrientationChange('portrait')}
                      className={`
                        flex flex-1 items-center justify-center gap-1 rounded-md px-2 py-1 text-[10px] font-semibold
                        ${localFormat.orientation === 'portrait'
                          ? 'bg-white text-indigo-700 shadow-sm'
                          : 'text-slate-600 hover:text-indigo-600'}
                      `}
                    >
                      <FormatIcon
                        aspectRatio={localFormat.aspectRatio}
                        orientation="portrait"
                        isSelected={localFormat.orientation === 'portrait'}
                      />
                      Portrait
                    </button>
                    <button
                      type="button"
                      onClick={() => handleOrientationChange('landscape')}
                      className={`
                        flex flex-1 items-center justify-center gap-1 rounded-md px-2 py-1 text-[10px] font-semibold
                        ${localFormat.orientation === 'landscape'
                          ? 'bg-white text-indigo-700 shadow-sm'
                          : 'text-slate-600 hover:text-indigo-600'}
                      `}
                    >
                      <FormatIcon
                        aspectRatio={localFormat.aspectRatio}
                        orientation="landscape"
                        isSelected={localFormat.orientation === 'landscape'}
                      />
                      Landscape
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ==================== MAIN CARD CONTAINER ==================== */}
      {/* This container is draggable by default (no nodrag) - BlinkinFlow pattern */}
      <div
        ref={containerRef}
        className="
          relative bg-white rounded-2xl overflow-visible
          border transition-all duration-300
          border-neutral-200/60
        "
        style={{
          // Dynamic shadow based on drag/select state (matches StickyNote and Shape)
          boxShadow: getShadowStyle(),
          // Smooth shadow transition + scale effect
          transition: dragging
            ? 'box-shadow 0.2s cubic-bezier(0.4, 0, 0.2, 1), transform 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
            : 'box-shadow 0.35s cubic-bezier(0.175, 0.885, 0.32, 1.275), transform 0.35s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
          // Slight scale up when dragging
          transform: dragging ? 'scale(1.01)' : 'scale(1)',
          minWidth: dimensions.width,
          minHeight,
          ...(isFixedSize ? { width: dimensions.width, height: dimensions.height } : {}),
        }}
      >
        {/* ==================== RESIZE HANDLES ==================== */}
        <NodeResizer
          minWidth={dimensions.width}
          minHeight={minHeight}
          isVisible={selected && !isFixedSize}
          lineClassName="border-indigo-500"
          handleClassName="bg-indigo-500 border-2 border-white rounded"
        />

        {/* ==================== CONTENT AREA ==================== */}
        {/* This wrapper provides padding - the padding area remains draggable */}
        {/* Scrollable content area with custom scrollbar styling */}
        <div
          className="relative px-5 pt-5 pb-2 overflow-y-auto overflow-x-hidden"
          style={{ height: '100%', zIndex: 1 }}
        >
          {/* ==================== EDITOR ==================== */}
          {/* Only the editor itself has nodrag - this prevents text selection from dragging */}
          {ydoc && awareness && user ? (
            <div className="nodrag nopan" style={{ minHeight: '180px', cursor: 'text' }}>
              <RichTextEditor
                ref={editorRef}
                ydoc={ydoc}
                awareness={awareness}
                currentUser={{
                  id: user.id,
                  name: user.name,
                  color: user.color,
                }}
                boardId={boardId}
                placeholder="Start typing, or use / for commands..."
                fieldName={`richtext-${id}`}
                mentionSuggestions={mentionSuggestions}
                mentionMode="nodes"
              />
            </div>
          ) : (
            <div className="text-gray-400" style={{ minHeight: '180px' }}>
              Loading editor...
            </div>
          )}

          {/* ==================== CONTEXT CHIPS (Incoming Connections) ==================== */}
          {connections.length > 0 && (
            <div className="bg-blue-50/50 rounded-xl p-3 border border-blue-100 mt-3">
              {/* Header with icon and label */}
              <div className="flex items-center gap-2 mb-2">
                <svg className="w-3.5 h-3.5 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                </svg>
                <span className="text-[10px] font-bold text-blue-500 uppercase tracking-wider">
                  Connected Data
                </span>
              </div>

              {/* Chips with click and drag interactions */}
              <div
                className="flex flex-wrap gap-1.5"
              >
                {connections.map((conn: BlockConnection, index: number) => (
                  <span
                    key={conn.nodeId}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleInsertData(conn);
                    }}
                    draggable
                    onDragStart={(e) => handleDragStart(e, index)}
                    onDrop={(e) => handleDrop(e, index)}
                    onDragOver={(e) => e.preventDefault()}
                    className="
                      nodrag nopan
                      inline-flex items-center gap-1 px-2 py-0.5
                      bg-blue-100 text-blue-600 text-[11px] font-medium
                      rounded-md cursor-pointer hover:bg-blue-200
                      transition-colors
                    "
                    style={{ opacity: draggedChipIndex === index ? 0.5 : 1 }}
                    title={`Click to insert ${conn.nodeLabel} | Drag to reorder`}
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-blue-400" />
                    {conn.nodeLabel}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* ==================== CITATIONS (From Knowledge Space answers) ==================== */}
          {citations && citations.length > 0 && (
            <div className="bg-cyan-50/50 rounded-xl p-3 border border-cyan-100 mt-3">
              {/* Header with icon and label */}
              <div className="flex items-center gap-2 mb-2">
                <svg className="w-3.5 h-3.5 text-cyan-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <span className="text-[10px] font-bold text-cyan-600 uppercase tracking-wider">
                  Sources
                </span>
              </div>

              {/* Citations list */}
              <div className="space-y-2">
                {citations.map((citation: KnowledgeCitation, index: number) => (
                  <div
                    key={`${citation.documentId}-${index}`}
                    className="flex items-start gap-2 p-2 rounded-lg bg-white border border-cyan-100"
                  >
                    {/* Citation index badge */}
                    <span className="flex-shrink-0 w-5 h-5 rounded-full bg-cyan-100 text-cyan-700 text-[10px] font-bold flex items-center justify-center">
                      {index + 1}
                    </span>
                    {/* Citation snippet */}
                    <p className="text-[11px] text-slate-600 line-clamp-2 flex-1">
                      {citation.snippet}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
});

// Display name for React DevTools
BlockNode.displayName = 'BlockNode';
